import { useState, useEffect, useRef } from 'react';

// Transition durations in ms
const FADE_OUT_DURATION = 150;
const FADE_IN_DURATION = 250;

export default function FadeTransition({ children, transitionKey }) {
  const [displayChildren, setDisplayChildren] = useState(children);
  const [opacity, setOpacity] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isFirstRender = useRef(true);
  const prevKeyRef = useRef(transitionKey);
  const pendingUpdate = useRef(null);

  useEffect(() => {
    // Skip transition on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayChildren(children);
      prevKeyRef.current = transitionKey;
      return;
    }

    // If key changed, start crossfade transition
    if (prevKeyRef.current !== transitionKey) {
      // Clear any pending update
      if (pendingUpdate.current) {
        clearTimeout(pendingUpdate.current);
      }

      setIsTransitioning(true);
      setOpacity(0);

      pendingUpdate.current = setTimeout(() => {
        // After fade out completes, swap to new content
        setDisplayChildren(children);
        prevKeyRef.current = transitionKey;

        // Use double RAF to ensure DOM has updated before fading in
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setOpacity(1);
            // Mark transition complete after fade-in finishes
            setTimeout(() => {
              setIsTransitioning(false);
            }, FADE_IN_DURATION);
          });
        });
        pendingUpdate.current = null;
      }, FADE_OUT_DURATION);

      return () => {
        if (pendingUpdate.current) {
          clearTimeout(pendingUpdate.current);
          pendingUpdate.current = null;
        }
      };
    } else if (!isTransitioning) {
      // Key didn't change and not transitioning, update children directly
      setDisplayChildren(children);
    }
  }, [transitionKey, children, isTransitioning]);

  return (
    <div
      className="transition-opacity ease-in-out"
      style={{
        opacity,
        transitionDuration: opacity === 0 ? `${FADE_OUT_DURATION}ms` : `${FADE_IN_DURATION}ms`,
      }}
    >
      {displayChildren}
    </div>
  );
}
